import React from 'react';

export interface ChipOption { value: string; label: string; }

export interface ChipGroupProps {
  id: string;
  label: string;
  options: ChipOption[];
  value: string | null;
  onChange: (value: string) => void;
}

/**
 * Single-select chip group with ARIA radiogroup semantics. One chip per
 * option; clicking selects it and unselects siblings (React state).
 *
 * ARIA contract:
 * - Container is a radiogroup with aria-label
 * - Each chip is a radio with aria-checked reflecting its selected state
 * - tabIndex management: the selected chip (or first if none) gets tabIndex=0;
 *   others get tabIndex=-1. Arrow keys move selection within the group.
 */
export const ChipGroup: React.FC<ChipGroupProps> = ({ id, label, options, value, onChange }) => {
  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
    const next = (idx + dir + options.length) % options.length;
    onChange(options[next].value);
    const nextEl = document.getElementById(`${id}-${options[next].value}`);
    nextEl?.focus();
  };
  const focusedValue = value ?? options[0]?.value;
  return (
    <div className="ai-chips" role="radiogroup" aria-label={label}>
      {options.map((opt, idx) => {
        const selected = value === opt.value;
        return (
          <button
            type="button"
            key={opt.value}
            id={`${id}-${opt.value}`}
            role="radio"
            aria-checked={selected}
            tabIndex={opt.value === focusedValue ? 0 : -1}
            className={`ai-chip${selected ? ' selected' : ''}`}
            onClick={() => onChange(opt.value)}
            onKeyDown={e => onKeyDown(e, idx)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

export default ChipGroup;
